import { c as _c } from "react/compiler-runtime";
import { useRequestErrorToast } from '@onefootprint/hooks';
import request from '@onefootprint/request';
import { useToast } from '@onefootprint/ui';
import isUndefined from 'lodash/isUndefined';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearWatchedRuns } from '@/components/agent-chat/components/run-completion-watcher/watched-runs';
import { resetAutoDecryptStores } from '@/entity/hooks/use-auto-decrypt-controls';
import { clearAllPlaybookEditSessions } from '@/hooks/use-in-progress-edit-session-storage';
import { DASHBOARD_ALLOW_ASSUMED_WRITES, DASHBOARD_AUTHORIZATION_HEADER, DASHBOARD_IS_LIVE_HEADER } from '../../config/constants';
import { getOrgMemberRequest } from '../use-get-org-member/use-get-org-member';
import type { AuthHeaders, MetaSession, OrgSession, Session, UserSession, UserSessionState } from './user-session.types';
import { defaultSession } from './user-session.types';
const logoutUser = (auth: string) => request({
  headers: {
    [DASHBOARD_AUTHORIZATION_HEADER]: auth
  },
  method: 'POST',
  url: '/org/auth/logout'
});
export const useStore = create<UserSessionState>()(persist(set => ({
  data: defaultSession,
  reset: () => set({
    data: defaultSession
  }),
  update: (data: Partial<Session>) => {
    set(oldSession => ({
      ...oldSession,
      data: {
        ...oldSession.data,
        ...data
      }
    }));
  }
}), {
  version: 13,
  name: 'dashboard-storage'
}));
type FetchNewPermissionsParams = {
  newAuthToken: string;
  newIsLive: boolean;
  newIsAssumedSessionEditMode?: boolean;
};
const useSession = () => {
  const $ = _c(62);
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const {
    data,
    reset,
    update
  } = useStore(_temp);
  const t0 = data.auth as string;
  const t1 = data.user as UserSession;
  const t2 = data.org as OrgSession;
  let t3;
  if ($[0] !== data.meta || $[1] !== t0 || $[2] !== t1 || $[3] !== t2) {
    t3 = {
      auth: t0,
      user: t1,
      org: t2,
      meta: data.meta
    };
    $[0] = data.meta;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const dangerouslyCastedData = t3;
  const isLoggedIn = !!data.user;
  const isLive = !!data.org?.isLive;
  const isAssumedSessionEditMode = !!data.user?.isAssumedSessionEditMode;
  const t4 = data?.auth as string;
  let t5;
  if ($[5] !== isLive) {
    t5 = JSON.stringify(isLive);
    $[5] = isLive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let authHeaders;
  if ($[7] !== isAssumedSessionEditMode || $[8] !== t4 || $[9] !== t5) {
    authHeaders = {
      [DASHBOARD_AUTHORIZATION_HEADER]: t4,
      [DASHBOARD_IS_LIVE_HEADER]: t5
    } as AuthHeaders;
    if (isAssumedSessionEditMode) {
      let t6;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = JSON.stringify(true);
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      authHeaders[DASHBOARD_ALLOW_ASSUMED_WRITES] = t6;
    }
    $[7] = isAssumedSessionEditMode;
    $[8] = t4;
    $[9] = t5;
    $[10] = authHeaders;
  } else {
    authHeaders = $[10];
  }
  let fetchNewPermissions;
  let fetchNewPermissionsOrLogout;
  let logIn;
  let logOut;
  if ($[12] !== data.auth || $[13] !== data.org?.isLive || $[14] !== isAssumedSessionEditMode || $[15] !== reset || $[16] !== showRequestErrorToast || $[17] !== toast || $[18] !== update) {
    logIn = async session => {
      if (session.meta) {
        update({
          meta: session.meta
        });
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: session.auth,
        newIsLive: session.newIsLive ?? data.org?.isLive ?? true,
        newIsAssumedSessionEditMode: false
      });
      resetAutoDecryptStores();
    };
    if ($[23] !== isAssumedSessionEditMode || $[24] !== toast || $[25] !== update) {
      fetchNewPermissions = async t6 => {
        const {
          newAuthToken,
          newIsLive,
          newIsAssumedSessionEditMode
        } = t6;
        const isAssumedSessionEditModeToSet = isUndefined(newIsAssumedSessionEditMode) ? isAssumedSessionEditMode : newIsAssumedSessionEditMode;
        const user = await getOrgMemberRequest({
          auth: newAuthToken,
          isLive: newIsLive,
          isAssumedSessionEditMode: !!isAssumedSessionEditModeToSet
        });
        if (user.tenant.isSandboxRestricted && newIsLive) {
          toast({
            title: "Switched to sandbox mode",
            description: "This organization doesn't have live mode enabled."
          });
          await fetchNewPermissions({
            newAuthToken,
            newIsLive: false,
            newIsAssumedSessionEditMode: isAssumedSessionEditModeToSet
          });
          return;
        }
        update({
          auth: newAuthToken,
          user: {
            ...user,
            isAssumedSession: !!user.isAssumedSession,
            isAssumedSessionEditMode: isAssumedSessionEditModeToSet
          },
          org: {
            ...user.tenant,
            isLive: newIsLive
          }
        });
      };
      $[23] = isAssumedSessionEditMode;
      $[24] = toast;
      $[25] = update;
      $[26] = fetchNewPermissions;
    } else {
      fetchNewPermissions = $[26];
    }
    fetchNewPermissionsOrLogout = async params => {
      ;
      try {
        await fetchNewPermissions(params);
      } catch (t6) {
        const error = t6;
        logOut();
        showRequestErrorToast(error);
      }
    };
    logOut = () => {
      if (data.auth) {
        logoutUser(data.auth).catch(console.error);
        resetAutoDecryptStores();
      }
      clearAllPlaybookEditSessions();
      clearWatchedRuns();
      reset();
    };
    $[12] = data.auth;
    $[13] = data.org?.isLive;
    $[14] = isAssumedSessionEditMode;
    $[15] = reset;
    $[16] = showRequestErrorToast;
    $[17] = toast;
    $[18] = update;
    $[19] = fetchNewPermissions;
    $[20] = fetchNewPermissionsOrLogout;
    $[21] = logIn;
    $[22] = logOut;
  } else {
    fetchNewPermissions = $[19];
    fetchNewPermissionsOrLogout = $[20];
    logIn = $[21];
    logOut = $[22];
  }
  let t6;
  if ($[27] !== data.user || $[28] !== update) {
    t6 = nextUser => {
      if (!data.user) {
        return;
      }
      update({
        user: {
          ...data.user,
          ...nextUser
        }
      });
    };
    $[27] = data.user;
    $[28] = update;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const updateUserName = t6;
  let t7;
  if ($[30] !== data.org || $[31] !== update) {
    t7 = nextOrg => {
      if (!data.org) {
        return;
      }
      update({
        org: {
          ...data.org,
          ...nextOrg
        }
      });
    };
    $[30] = data.org;
    $[31] = update;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const setOrg = t7;
  let t8;
  if ($[33] !== data.auth || $[34] !== fetchNewPermissionsOrLogout || $[35] !== isLive) {
    t8 = async newEditMode => {
      if (!data?.auth) {
        return;
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: data.auth,
        newIsLive: isLive,
        newIsAssumedSessionEditMode: newEditMode
      });
    };
    $[33] = data.auth;
    $[34] = fetchNewPermissionsOrLogout;
    $[35] = isLive;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const setAssumedSessionEditMode = t8;
  let t9;
  if ($[37] !== data.auth || $[38] !== fetchNewPermissionsOrLogout) {
    t9 = async newIsLive_0 => {
      if (!data.auth) {
        return;
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: data.auth,
        newIsLive: newIsLive_0
      });
    };
    $[37] = data.auth;
    $[38] = fetchNewPermissionsOrLogout;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const setIsLive = t9;
  let t10;
  if ($[40] !== data.meta || $[41] !== update) {
    t10 = () => {
      update({
        meta: {
          ...data.meta,
          requiresOnboarding: false
        }
      });
    };
    $[40] = data.meta;
    $[41] = update;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const completeOnboarding = t10;
  let t11;
  if ($[43] !== data.auth || $[44] !== fetchNewPermissions || $[45] !== isLive) {
    t11 = async () => {
      if (!data.auth) {
        return;
      }
      await fetchNewPermissions({
        newAuthToken: data.auth,
        newIsLive: isLive
      });
    };
    $[43] = data.auth;
    $[44] = fetchNewPermissions;
    $[45] = isLive;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  const refreshPermissions = t11;
  let t12;
  if ($[47] !== authHeaders || $[48] !== completeOnboarding || $[49] !== dangerouslyCastedData || $[50] !== data || $[51] !== isAssumedSessionEditMode || $[52] !== isLive || $[53] !== isLoggedIn || $[54] !== logIn || $[55] !== logOut || $[56] !== refreshPermissions || $[57] !== setAssumedSessionEditMode || $[58] !== setIsLive || $[59] !== setOrg || $[60] !== updateUserName) {
    t12 = {
      authHeaders,
      dangerouslyCastedData,
      data,
      isLoggedIn,
      logIn,
      logOut,
      refreshPermissions,
      setOrg,
      updateUserName,
      isLive,
      setIsLive,
      isAssumedSessionEditMode,
      setAssumedSessionEditMode,
      completeOnboarding
    };
    $[47] = authHeaders;
    $[48] = completeOnboarding;
    $[49] = dangerouslyCastedData;
    $[50] = data;
    $[51] = isAssumedSessionEditMode;
    $[52] = isLive;
    $[53] = isLoggedIn;
    $[54] = logIn;
    $[55] = logOut;
    $[56] = refreshPermissions;
    $[57] = setAssumedSessionEditMode;
    $[58] = setIsLive;
    $[59] = setOrg;
    $[60] = updateUserName;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  return t12;
};
export default useSession;
function _temp(state) {
  return state;
}